/*
====================================================================
ClientIdGate.jsx

Shown before any non-registration form. The patient identifies
themselves with EITHER their Client ID or the email they registered
with; their record is loaded and handed to DynamicForm, which shows the
known details read-only and asks only for the remaining fields. Those
remaining answers are what ends up in form_submissions.

WHY EITHER
----------
The Client ID arrives by email at registration and on a slip from
reception, so some patients have it to hand and some don't. An email
address is the one thing they can always remember. One field takes both:
if it contains "@" the server looks up by email, otherwise by id.

THE MOBILE NUMBER IS OPTIONAL
-----------------------------
Client ids are "3" followed by 8 random digits, so there is no short
sequence to walk. The mobile stays here as a second check for anyone who
has it: the server verifies it WHEN GIVEN and rejects a wrong one, but
accepts a blank. See REQUIRE_MOBILE_MATCH in form.controller.js.

Registration forms never reach this screen: there is no client yet,
which is the point of registering.
====================================================================
*/

import React, { useState, useEffect } from "react";

import { lookupClient } from "./formApi";
import { recallRegistration } from "./Registrationsession";

import "./DynamicForm.css";

/*
  The gate is the first thing a patient sees on a phone, and it has two
  inputs the clinic needs read correctly. 16px is the floor: below it,
  iOS Safari zooms the page on focus and does not zoom back, so the
  second field lands off screen.

  Scoped under .df-page like everything else here.
*/
const GATE_CSS = `
@media (max-width: 640px) {
  .df-page .df-field-grid { display: block; }
  .df-page .df-field { width: 100% !important; margin-bottom: 14px; }
  .df-page .df-input { font-size: 16px; max-width: 100%; }
  .df-page .df-submit-btn { width: 100%; }
}
`;

/*
  Wording comes from the category — "patient" on an Aesthetic form,
  "client" on Yoga or Sujok. SingleFormPage passes it in; the default
  keeps this component usable on its own.
*/
const DEFAULT_TERMS = {
    person: "patient",
    Person: "Patient",
    idLabel: "Client ID",
    formNoun: "patient form"
};

const ClientIdGate = ({ token, formName, terms = DEFAULT_TERMS, onResolved }) => {

    /*
      Force "Client ID" everywhere on this screen. The parent
      (SingleFormPage / FormCategories) may pass "Patient ID" for medical
      or aesthetic forms; we ignore that here so every form category shows
      the same identifier label. Delete this line to fall back to the
      per-category wording.
    */
    const idLabel = "Client ID";

    const [identifier, setIdentifier] = useState("");
    const [mobile, setMobile] = useState("");
    const [checking, setChecking] = useState(false);
    const [error, setError] = useState("");

    // Who registered on this device a moment ago, if anyone.
    const [recalled, setRecalled] = useState(null);

    /*
      Pre-fill from the registration just completed in this tab, so a
      patient who was handed a Client ID thirty seconds ago does not
      have to type it back in.

      PRE-FILLED, NOT AUTO-SUBMITTED. The gate still appears and the
      patient still presses Continue, which is the one moment they see
      whose record is about to open. On a tablet passed between people
      that check is worth a single tap.
    */
    useEffect(() => {
        const prior = recallRegistration(token);

        if (prior && prior.clientId) {
            setRecalled(prior);
            setIdentifier(prior.clientId);
        }
    }, [token]);

    const submit = async (e) => {
        if (e) e.preventDefault();

        const who = identifier.trim();

        if (!who) {
            setError(`Please enter your ${idLabel} or registered email.`);
            return;
        }

        try {
            setChecking(true);
            setError("");

            const res = await lookupClient(token, {
                // `identifier` is what the server reads now; client_id is sent
                // too so an older deployed backend still works unchanged.
                identifier: who,
                client_id: who,
                mobile: mobile.trim()
            });

            onResolved(res.data.client);

        } catch (err) {
            console.log("Client lookup failed", err);
            setError(
                err.response?.data?.message ||
                "Could not verify those details. Please check with reception."
            );
        } finally {
            setChecking(false);
        }
    };

    return (
        <div className="df-page">
            <style>{GATE_CSS}</style>
            <div className="df-card">

                <div className="df-header">
                    <h2>{formName || terms.formNoun}</h2>
                    <p>
                        Enter your {idLabel} or registered email to load
                        this form.
                    </p>
                </div>

                <form className="df-body" onSubmit={submit}>
                    <div className="df-section">
                        <div className="df-field-grid">

                            <div className="df-field df-field-full">
                                <span className="df-label">
                                    {idLabel} or registered email
                                    <span className="df-required">*</span>
                                </span>
                                <input
                                    className="df-input"
                                    autoComplete="off"
                                    autoCapitalize="none"
                                    spellCheck="false"
                                    value={identifier}
                                    onChange={(e) => setIdentifier(e.target.value)}
                                    placeholder="Client ID (e.g. 312345678) or you@example.com"
                                />
                            </div>

                            <div className="df-field">
                                <span className="df-label">
                                    Registered mobile number{" "}
                                    <span style={{ color: "#7c8a8d", fontWeight: 400 }}>
                                        (optional)
                                    </span>
                                </span>
                                <input
                                    className="df-input"
                                    inputMode="tel"
                                    autoComplete="tel"
                                    value={mobile}
                                    onChange={(e) => setMobile(e.target.value)}
                                    placeholder="10-digit number"
                                />
                            </div>

                        </div>

                        {recalled && identifier === recalled.clientId && !error && (
                            <p style={{
                                color: "#172056",
                                background: "#F1F3F8",
                                border: "1px solid #CDD3E5",
                                borderRadius: 8,
                                padding: "10px 12px",
                                marginTop: 12,
                                fontSize: 13.5
                            }}>
                                Continuing as <b>{recalled.name || "the patient who just registered"}</b>.
                                Not you? Clear the box and enter your own details.
                            </p>
                        )}

                        {error && (
                            <p style={{ color: "#c0392b", marginTop: 12 }}>{error}</p>
                        )}

                        <p style={{ color: "#7c8a8d", fontSize: 13, marginTop: 12 }}>
                            Don’t have a {idLabel}? Please complete the registration
                            form first, or ask at the reception desk.
                        </p>
                    </div>

                    <div className="df-submit-bar">
                        <button
                            className="df-submit-btn"
                            type="submit"
                            disabled={checking || !identifier.trim()}
                        >
                            {checking ? "Checking…" : "Continue"}
                        </button>
                    </div>
                </form>

            </div>
        </div>
    );
};

export default ClientIdGate;